import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import MLIcon from '../../../../MLIcon/MLIcon';
import MLTooltip from '../../../../MLTooltip';
import styles from './StudentCell.module.css';

export function StudentCell({
  firstName,
  lastName,
  email,
  studentId,
  onStudentClick,
  isBrokenSyncReefGrades,
}) {
  const [isCopied, setIsCopied] = useState(false);

  const handleClick = useCallback(() => {
    if (onStudentClick && studentId) {
      onStudentClick(studentId);
    }
  }, [onStudentClick, studentId]);

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && onStudentClick) {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick, onStudentClick],
  );

  const copyToClipboard = useCallback(
    (event) => {
      event.stopPropagation();
      navigator.clipboard.writeText(email);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    },
    [email],
  );

  const handleEmailKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        copyToClipboard(e);
      }
    },
    [copyToClipboard],
  );

  const isClickable = !!onStudentClick && !!studentId;
  const fullName = `${firstName} ${lastName}`.trim();

  const containerClassName = isBrokenSyncReefGrades
    ? `${styles.container} ${styles.brokenReefSync}`
    : styles.container;

  return (
    <div className={containerClassName}>
      <div className={styles.nameEmailContainer}>
        <div className={styles.nameEmailInnerContainer}>
          {isClickable ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              className={styles.name}
              aria-label={`View gradebook for ${fullName}`}
              data-test-id={`${firstName}-student-name`}
            >
              <span>{firstName}</span>
              <span>{lastName}</span>
            </button>
          ) : (
            <div
              className={styles.name}
              data-test-id={`${firstName}-student-name`}
            >
              <span>{firstName}</span>
              <span>{lastName}</span>
            </div>
          )}
          {email && (
            <MLTooltip
              interactive={true}
              content={
                <div className={styles.copyEmailTooltip}>
                  <button
                    type="button"
                    tabIndex={-1}
                    className={styles.copyEmailButton}
                    onClick={copyToClipboard}
                  >
                    <MLIcon
                      type={isCopied ? 'checkmark' : 'item_copy'}
                      role="img"
                      className={styles.tooltipIcon}
                    />
                    {isCopied ? 'Copied!' : 'Copy email to clipboard'}
                  </button>
                </div>
              }
            >
              <div
                className={styles.email}
                tabIndex={-1}
                role="button"
                aria-label={`Copy ${email} to clipboard`}
                onClick={copyToClipboard}
                onKeyDown={handleEmailKeyDown}
                data-test-id={`${firstName}-student-email`}
              >
                {email}
              </div>
            </MLTooltip>
          )}
        </div>
        {isBrokenSyncReefGrades && (
          <MLTooltip
            content={
              <div className={styles.iClickerMessage}>
                iClicker Connection
                <br />
                Not Established
              </div>
            }
          >
            <div
              className={styles.brokenReefSyncContainer}
              tabIndex={-1}
              role="img"
              aria-label="iClicker connection not established"
              data-test-id={`${firstName}-iclicker-disconnected`}
            >
              <MLIcon
                viewBox="0 0 40 40"
                type="unlink"
                role="presentation"
                className={styles.brokenReefSyncIcon}
              />
            </div>
          </MLTooltip>
        )}
      </div>
    </div>
  );
}

StudentCell.propTypes = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string,
  studentId: PropTypes.string,
  onStudentClick: PropTypes.func,
  isBrokenSyncReefGrades: PropTypes.bool,
};

StudentCell.defaultProps = {
  email: '',
  studentId: '',
  onStudentClick: null,
  isBrokenSyncReefGrades: false,
};
